'use client';

import React, { useEffect, useRef, useState } from "react";
import {BuilderBlocks, type BuilderElement} from "@builder.io/react";
import { cn } from "@/lib/utils";
import { SpacingType, sectionVariants } from "@/types";
import { SectionWrapper } from "../components/SectionWraper";

interface ArticleProps {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  withLeftColumn?: boolean;
  withRightColumn?: boolean;
  content?: BuilderElement[];
  leftColumn?: BuilderElement[];  
  rightColumn?: BuilderElement[];
  showIndexBlock?: boolean;
  builderBlock?: BuilderElement;
  attributes?: React.HTMLProps<HTMLElement>;
}

export const Article = ({
  darkMode = false,
  variant = 'A',
  usedInPageNav = false,
  pageNavTitle = "Article",
  themeChangeOnScroll = false,
  withLeftColumn,
  withRightColumn,
  content,
  leftColumn,
  rightColumn,
  showIndexBlock,
  builderBlock,
  spacing = "small",
  attributes,
}: ArticleProps) => {
  const builderContentRef = useRef<HTMLDivElement>(null);
  const [allTrackableHeadings, setAllTrackableHeadings] = useState<{ id: string; title: string; }[]>([]);
  const [activeTrackableHeadings, setActiveTrackableHeadings] = useState<{ id: string; title: string; }[]>([]);

  useEffect(() => {
    if (!builderContentRef.current) return;
  
    const headingElements = Array.from(
      builderContentRef.current.querySelectorAll('[data-trackable="true"]')
    ) as HTMLElement[];
  
    const headings = headingElements
      .map(el => {
        const trackableTitle = el.getAttribute("data-trackable-title");
        const textTitle = el.getAttribute("data-text-title");
        const id = el.id;
        if (!trackableTitle || !id) return null;
  
        // Use data-text-title for display, fallback to trackableTitle if not available
        const title = textTitle || trackableTitle
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
  
        return { id, title };
      })
      .filter((heading): heading is { id: string; title: string } => heading !== null);
  
    setAllTrackableHeadings(headings);
  
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      const visibleHeadings: { id: string; title: string; top: number }[] = [];
  
      entries.forEach(entry => {
        const el = entry.target as HTMLElement;
        const trackableTitle = el.getAttribute("data-trackable-title");
        const textTitle = el.getAttribute("data-text-title");
        const id = el.id;
        if (!trackableTitle || !id) return;
  
        // Use data-text-title for display, fallback to trackableTitle if not available
        const title = textTitle || trackableTitle
          .split('-')
          .map(w => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');
  
        if (entry.isIntersecting) {
          const rect = el.getBoundingClientRect();
          visibleHeadings.push({ id, title, top: rect.top });
        }
      });
  
      if (visibleHeadings.length > 0) {
        // Sort by position on screen (top-most first)
        visibleHeadings.sort((a, b) => a.top - b.top);
        const closest = visibleHeadings[0];
  
        setActiveTrackableHeadings(prev => {
          if (prev.length === 1 && prev[0].id === closest.id) return prev;
          return [closest];
        });
      }
    };
  
    const observer = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: "0px",
      threshold: 1,
    });
  
    headingElements.forEach(el => observer.observe(el));
  
    return () => {
      observer.disconnect();
    };
  }, [content]);
  
  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
  
    if (element) {
      const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
      const offset = - 96;
      window.scrollTo({
        top: elementTop + offset,
        behavior: 'smooth',
      });
    }
  };

  const leftColumnClass = cn(
    "top-24 flex h-fit w-full flex-col gap-5 lg:sticky order-1",
    withRightColumn 
      ? "lg:col-span-2 lg:max-w-65 xl:max-w-full xl:col-span-1 xl:min-w-[240px]" 
      : "lg:col-span-2 lg:max-w-100 xl:min-w-[240px]"
  );

  const contentClass = cn(
    withRightColumn && withLeftColumn
      ? "max-w-[65ch] lg:mx-auto"
      : "max-w-[85ch]",
    !withRightColumn && withLeftColumn ? "lg:ml-auto" : "",
    !withLeftColumn && !withRightColumn ? "lg:mx-auto" : ""
  );

  const rightColumnClass = cn(
    "lg:sticky top-24 h-fit order-1 lg:order-3",
    withLeftColumn ? "hidden xl:block" : "",
    !withLeftColumn ? "lg:col-span-2 w-full lg:max-w-[362px] xl:min-w-[240px] ml-auto" : "",
  );
  
  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="article"
      attributes={attributes}
    > 
      <div className="container">
        <div className="relative grid gap-10 lg:grid-cols-5">

          {/* Left Column */}
          {withLeftColumn && (
            <aside className={leftColumnClass}>
              <BuilderBlocks
                parentElementId={builderBlock?.id}
                dataPath="leftColumn"
                blocks={leftColumn ?? []}
                /> 
            </aside>
          )}
        
          {/* Content Column */}
          {content && (
            <div className={cn(
              "order-2",
              withRightColumn || withLeftColumn
                ? "lg:col-span-3"
                : "lg:col-span-5",
              !withRightColumn && !withLeftColumn ? "lg:col-span-5" : ""
            )}>
              <div className={contentClass} ref={builderContentRef}>
                {/* Render child Builder blocks */}
                <BuilderBlocks
                  className="gap-6"
                  parentElementId={builderBlock?.id}
                  dataPath="content"
                  blocks={content ?? []}
                />
              </div> 
            </div>
          )}
          
          {/* Right Column */}
          {withRightColumn && (
            <aside className={rightColumnClass}>
              {showIndexBlock && allTrackableHeadings.length > 0 && (
                <> 
                  <p className="paragraph-base font-bold text-primary">On this page</p>
                  <ul className="mt-1.5 paragraph-xs text-muted-foreground mb-10">
                    {allTrackableHeadings.map((item) => (
                      <li key={item.id}>
                        <a
                          href={`#${item.id}`}
                          onClick={(e) => handleAnchorClick(e, item.id)}
                          className={cn(
                            "paragraph-sm text-transparent bg-cover bg-center bg-clip-text bg-primary block border-l border-border reserve:border-[#D1D5DC]! py-2 pl-4 transition-all duration-300",
                            activeTrackableHeadings.some((h) => h.id === item.id)
                              ? "border-gradient-primary bg-[image:var(--color-gradient-primary)]"
                              : "bg-[image:var(--color-primary)] hover:bg-[image:var(--color-gradient-primary)] bg-[length:100%_100%]",
                            activeTrackableHeadings.some((h) => h.id === item.id)
                              ? "reserve:bg-[image:linear-gradient(#7a7068,#7a7068)]! reserve:border-[#7a7068]! reserve:border-image-none! reserve:underline! reserve:underline-offset-2! reserve:hover:bg-[image:linear-gradient(#7a7068,#7a7068)]!"
                              : "reserve:hover:bg-[image:linear-gradient(#7a7068,#7a7068)]!"
                          )}
                        >
                          {item.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </> 
              )}
              <BuilderBlocks
                parentElementId={builderBlock?.id}
                dataPath="rightColumn"
                blocks={rightColumn ?? []}
              />
            </aside>
          )}
        </div>
      </div>
    </SectionWrapper>
  );
};
